import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../../components/layout/PageShell";
import {
    getPlatformSettingsByPrefix,
    savePlatformSettings
} from "../../services/crmService";

import "../Setting.css";

const DEFAULTS = {
    "generation.minPublishQualityScore": 75,
    "generation.autoRepairBelowScore": 80,
    "generation.maxRepairAttempts": 2,
    "generation.enableCriticDrivenRepair": true,
    "generation.enableGenericContentDetection": true,
    "generation.enableVerticalDensityValidation": true,

    "crm.aiGenerateMode": "append-suggestions",
    "crm.defaultSuggestionCount": 3,
    "crm.requireInstructionWhenItemsExist": true,
    "crm.preventDuplicateModuleItems": true,
    "crm.autoPublishAiSuggestions": false,

    "deployment.autoRedeployAfterCrmPublish": true,
    "deployment.enableVersionedDeployments": true,
    "deployment.enableAssetCacheBusting": true,
    "deployment.keepLastVersions": 5,

    "ai.limits.enableLimitEnforcement": true,
    "ai.limits.dailyTextCallsPerAccount": 100,
    "ai.limits.dailyImageCallsPerAccount": 25,
    "ai.limits.monthlyTextCallsPerAccount": 1000,
    "ai.limits.monthlyImageCallsPerAccount": 200,
    "ai.limits.superAdminBypass": true
};

function parseSettingValue(setting, fallback) {
    if (!setting || setting.settingValueJson === undefined || setting.settingValueJson === null) {
        return fallback;
    }

    try {
        return JSON.parse(setting.settingValueJson);
    } catch {
        return fallback;
    }
}

function toSettingValueJson(value) {
    return JSON.stringify(value);
}

function toNumber(value, fallback = 0) {
    const next = Number(value);

    if (Number.isNaN(next)) {
        return fallback;
    }

    return next;
}

export default function AdminGenerationSettingsPage() {
    const [form, setForm] = useState(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadSettings = useCallback(async () => {
        try {
            setLoading(true);

            const prefixes = await Promise.all([
                getPlatformSettingsByPrefix("generation."),
                getPlatformSettingsByPrefix("crm."),
                getPlatformSettingsByPrefix("deployment."),
                getPlatformSettingsByPrefix("ai.limits.")
            ]);

            const rows = prefixes.flatMap(x => Array.isArray(x) ? x : []);

            const next = { ...DEFAULTS };

            rows.forEach(setting => {
                const key = setting.settingKey;

                if (Object.prototype.hasOwnProperty.call(next, key)) {
                    next[key] = parseSettingValue(setting, DEFAULTS[key]);
                }
            });

            setForm(next);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load platform settings.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const setValue = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const buildSetting = (key, value, valueType = "json") => ({
        scope: "global",
        accountId: null,
        projectId: null,
        settingKey: key,
        settingValueJson: toSettingValueJson(value),
        valueType,
        isSecret: false,
        isSystem: true
    });

    const handleSave = async () => {
        try {
            setSaving(true);

            const payload = [
                buildSetting(
                    "generation.minPublishQualityScore",
                    toNumber(form["generation.minPublishQualityScore"], 75),
                    "number"
                ),
                buildSetting(
                    "generation.autoRepairBelowScore",
                    toNumber(form["generation.autoRepairBelowScore"], 80),
                    "number"
                ),
                buildSetting(
                    "generation.maxRepairAttempts",
                    toNumber(form["generation.maxRepairAttempts"], 2),
                    "number"
                ),
                buildSetting(
                    "generation.enableCriticDrivenRepair",
                    !!form["generation.enableCriticDrivenRepair"],
                    "bool"
                ),
                buildSetting(
                    "generation.enableGenericContentDetection",
                    !!form["generation.enableGenericContentDetection"],
                    "bool"
                ),
                buildSetting(
                    "generation.enableVerticalDensityValidation",
                    !!form["generation.enableVerticalDensityValidation"],
                    "bool"
                ),

                buildSetting(
                    "crm.aiGenerateMode",
                    form["crm.aiGenerateMode"] || "append-suggestions",
                    "string"
                ),
                buildSetting(
                    "crm.defaultSuggestionCount",
                    toNumber(form["crm.defaultSuggestionCount"], 3),
                    "number"
                ),
                buildSetting(
                    "crm.requireInstructionWhenItemsExist",
                    !!form["crm.requireInstructionWhenItemsExist"],
                    "bool"
                ),
                buildSetting(
                    "crm.preventDuplicateModuleItems",
                    !!form["crm.preventDuplicateModuleItems"],
                    "bool"
                ),
                buildSetting(
                    "crm.autoPublishAiSuggestions",
                    !!form["crm.autoPublishAiSuggestions"],
                    "bool"
                ),

                buildSetting(
                    "deployment.autoRedeployAfterCrmPublish",
                    !!form["deployment.autoRedeployAfterCrmPublish"],
                    "bool"
                ),
                buildSetting(
                    "deployment.enableVersionedDeployments",
                    !!form["deployment.enableVersionedDeployments"],
                    "bool"
                ),
                buildSetting(
                    "deployment.enableAssetCacheBusting",
                    !!form["deployment.enableAssetCacheBusting"],
                    "bool"
                ),
                buildSetting(
                    "deployment.keepLastVersions",
                    toNumber(form["deployment.keepLastVersions"], 5),
                    "number"
                ),

                buildSetting(
                    "ai.limits.enableLimitEnforcement",
                    !!form["ai.limits.enableLimitEnforcement"],
                    "bool"
                ),
                buildSetting(
                    "ai.limits.dailyTextCallsPerAccount",
                    toNumber(form["ai.limits.dailyTextCallsPerAccount"], 100),
                    "number"
                ),
                buildSetting(
                    "ai.limits.dailyImageCallsPerAccount",
                    toNumber(form["ai.limits.dailyImageCallsPerAccount"], 25),
                    "number"
                ),
                buildSetting(
                    "ai.limits.monthlyTextCallsPerAccount",
                    toNumber(form["ai.limits.monthlyTextCallsPerAccount"], 1000),
                    "number"
                ),
                buildSetting(
                    "ai.limits.monthlyImageCallsPerAccount",
                    toNumber(form["ai.limits.monthlyImageCallsPerAccount"], 200),
                    "number"
                ),
                buildSetting(
                    "ai.limits.superAdminBypass",
                    !!form["ai.limits.superAdminBypass"],
                    "bool"
                )
            ];

            await savePlatformSettings(payload);

            toast.success("Platform settings saved.");
            await loadSettings();
        } catch (err) {
            console.error(err);
            toast.error("Failed to save platform settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageShell title="Generation & CRM Rules">
                <div className="project-loading-card">Loading platform settings...</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Generation & CRM Rules">
            <div className="settings-shell">
                <section className="project-hero-card">
                    <div>
                        <span className="project-kicker">Platform Settings</span>
                        <h1>Generation, CRM, Deployment & AI Limits</h1>
                        <p>
                            Tune quality scoring, CRM AI behavior, deployment versioning,
                            and account-level AI usage limits.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="project-action-btn primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </section>

                <section className="settings-form-grid">
                    <div className="project-panel">
                        <span className="project-kicker">Quality</span>
                        <h2>Generation Quality</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Minimum Publish Score</span>
                                <input
                                    type="number"
                                    value={form["generation.minPublishQualityScore"]}
                                    onChange={e =>
                                        setValue(
                                            "generation.minPublishQualityScore",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>

                            <label>
                                <span>Auto Repair Below Score</span>
                                <input
                                    type="number"
                                    value={form["generation.autoRepairBelowScore"]}
                                    onChange={e =>
                                        setValue(
                                            "generation.autoRepairBelowScore",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>

                            <label>
                                <span>Max Repair Attempts</span>
                                <input
                                    type="number"
                                    value={form["generation.maxRepairAttempts"]}
                                    onChange={e =>
                                        setValue(
                                            "generation.maxRepairAttempts",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>
                        </div>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["generation.enableCriticDrivenRepair"]}
                                    onChange={e =>
                                        setValue(
                                            "generation.enableCriticDrivenRepair",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Enable critic-driven repair</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["generation.enableGenericContentDetection"]}
                                    onChange={e =>
                                        setValue(
                                            "generation.enableGenericContentDetection",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Enable generic content detection</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["generation.enableVerticalDensityValidation"]}
                                    onChange={e =>
                                        setValue(
                                            "generation.enableVerticalDensityValidation",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Enable vertical density validation</span>
                            </label>
                        </div>
                    </div>

                    <div className="project-panel">
                        <span className="project-kicker">CRM AI</span>
                        <h2>Module Generation</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Default AI Generate Mode</span>
                                <select
                                    value={form["crm.aiGenerateMode"]}
                                    onChange={e =>
                                        setValue("crm.aiGenerateMode", e.target.value)
                                    }
                                >
                                    <option value="append-suggestions">Append Suggestions</option>
                                    <option value="replace-existing">Replace Existing</option>
                                    <option value="ask-user">Ask User</option>
                                </select>
                            </label>

                            <label>
                                <span>Default Suggestion Count</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={form["crm.defaultSuggestionCount"]}
                                    onChange={e =>
                                        setValue(
                                            "crm.defaultSuggestionCount",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>
                        </div>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["crm.requireInstructionWhenItemsExist"]}
                                    onChange={e =>
                                        setValue(
                                            "crm.requireInstructionWhenItemsExist",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Require instruction when items already exist</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["crm.preventDuplicateModuleItems"]}
                                    onChange={e =>
                                        setValue(
                                            "crm.preventDuplicateModuleItems",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Prevent duplicate module items</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["crm.autoPublishAiSuggestions"]}
                                    onChange={e =>
                                        setValue(
                                            "crm.autoPublishAiSuggestions",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Auto-publish AI suggestions</span>
                            </label>
                        </div>
                    </div>

                    <div className="project-panel">
                        <span className="project-kicker">Deployment</span>
                        <h2>Publish & Recovery</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Keep Last Versions</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="25"
                                    value={form["deployment.keepLastVersions"]}
                                    onChange={e =>
                                        setValue(
                                            "deployment.keepLastVersions",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>
                        </div>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["deployment.autoRedeployAfterCrmPublish"]}
                                    onChange={e =>
                                        setValue(
                                            "deployment.autoRedeployAfterCrmPublish",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Auto redeploy after CRM publish</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["deployment.enableVersionedDeployments"]}
                                    onChange={e =>
                                        setValue(
                                            "deployment.enableVersionedDeployments",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Enable versioned deployments</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["deployment.enableAssetCacheBusting"]}
                                    onChange={e =>
                                        setValue(
                                            "deployment.enableAssetCacheBusting",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Enable asset cache busting</span>
                            </label>
                        </div>
                    </div>

                    <div className="project-panel">
                        <span className="project-kicker">AI Cost Control</span>
                        <h2>Usage Limits</h2>

                        <p className="settings-muted">
                            Enforce account-level AI call limits before provider calls happen.
                            Blocked calls are logged with provider <strong>usage-limit</strong>.
                        </p>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["ai.limits.enableLimitEnforcement"]}
                                    onChange={e =>
                                        setValue(
                                            "ai.limits.enableLimitEnforcement",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Enable AI usage limit enforcement</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!form["ai.limits.superAdminBypass"]}
                                    onChange={e =>
                                        setValue(
                                            "ai.limits.superAdminBypass",
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Allow super admin bypass</span>
                            </label>
                        </div>

                        <div className="settings-field-grid">
                            <label>
                                <span>Daily Text Calls / Account</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={form["ai.limits.dailyTextCallsPerAccount"]}
                                    onChange={e =>
                                        setValue(
                                            "ai.limits.dailyTextCallsPerAccount",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>

                            <label>
                                <span>Daily Image Calls / Account</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={form["ai.limits.dailyImageCallsPerAccount"]}
                                    onChange={e =>
                                        setValue(
                                            "ai.limits.dailyImageCallsPerAccount",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>

                            <label>
                                <span>Monthly Text Calls / Account</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={form["ai.limits.monthlyTextCallsPerAccount"]}
                                    onChange={e =>
                                        setValue(
                                            "ai.limits.monthlyTextCallsPerAccount",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>

                            <label>
                                <span>Monthly Image Calls / Account</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={form["ai.limits.monthlyImageCallsPerAccount"]}
                                    onChange={e =>
                                        setValue(
                                            "ai.limits.monthlyImageCallsPerAccount",
                                            e.target.value
                                        )
                                    }
                                />
                            </label>
                        </div>

                        <div className="settings-warning-box">
                            For testing, set daily text/image limit to <strong>1</strong>.
                            For beta production, keep sensible values like text 100/day and image 25/day.
                        </div>
                    </div>
                </section>

                <div className="settings-sticky-save">
                    <button
                        type="button"
                        className="project-action-btn primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </div>
            </div>
        </PageShell>
    );
}